import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { api, extractErrorMessage } from '../../api/client';
import { useToast } from '../../components/Toast';
import { Button } from '../../components/Button';
import { Badge } from '../../components/Badge';
import { HelpTip } from '../../components/HelpTip';
import type { EstadoCobrosMp } from './types';

/** Cobros de MercadoPago (fecha real de liberación): estado de la sincronización y botón (sólo admin). Sólo LEE de ML/MP. */
export function CobrosMercadoPagoPanel({ onCambio }: { onCambio: () => void }) {
  const { user } = useAuth();
  const esAdmin = user?.role === 'admin';
  const toast = useToast();
  const [estado, setEstado] = useState<EstadoCobrosMp | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [iniciando, setIniciando] = useState(false);
  // Cuenta cada consulta terminada: re-arma el temporizador aunque el estado no cambie.
  const [consultas, setConsultas] = useState(0);

  const cargar = useCallback(() => {
    api.get<EstadoCobrosMp>('/api/v3/finanzas/cobros-mercadopago')
      .then(e => { setEstado(e); setError(null); })
      .catch(e => setError(extractErrorMessage(e)))
      .finally(() => setConsultas(n => n + 1));
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  // Mientras corre, refresca cada 3 s; al terminar recarga el flujo (cambian los cobros proyectados).
  const [estabaEnCurso, setEstabaEnCurso] = useState(false);
  useEffect(() => {
    if (estado?.enCurso) {
      setEstabaEnCurso(true);
      const t = setTimeout(cargar, 3000);
      return () => clearTimeout(t);
    }
    if (estabaEnCurso && estado && !estado.enCurso) {
      setEstabaEnCurso(false);
      onCambio();
    }
  }, [estado, consultas, cargar, estabaEnCurso, onCambio]);

  const actualizar = async () => {
    setIniciando(true);
    try {
      const r = await api.post<{ iniciado: boolean; estado: EstadoCobrosMp }>('/api/v3/finanzas/cobros-mercadopago/actualizar', {});
      setEstado(r.estado);
      toast.addToast(r.iniciado ? 'Actualizando cobros de MercadoPago (sólo lectura)…' : 'Ya hay una actualización en curso', 'info');
    } catch (e) {
      toast.addToast(extractErrorMessage(e), 'error');
    } finally {
      setIniciando(false);
    }
  };

  const r = estado?.reporte;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="mb-3 flex items-center gap-2">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Cobros de MercadoPago</h2>
        <HelpTip tips={[
          'Pagos de las ventas de MercadoLibre con la fecha REAL en que MercadoPago libera el dinero; se proyectan en "Cobros de MercadoLibre"',
          'Sólo se lee de MercadoLibre y MercadoPago: no se modifica nada',
          'Se consultan las órdenes pagadas de los últimos 45 días sin cobros guardados, y los cobros no liberados cada 12 h',
          estado?.horario ?? 'Se actualiza sola todos los días.',
        ]} />
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
      {!estado && !error && <p className="text-sm text-slate-400">Cargando…</p>}
      {estado && (
        <div className="space-y-3 text-sm text-slate-700">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-1">
            <span>Última actualización: <strong>{estado.ultimaCorrida ? new Date(estado.ultimaCorrida).toLocaleString('es-AR') : 'nunca'}</strong>
              {r && ` (${r.origen === 'cron' ? 'automática' : 'manual'})`}</span>
            <span>Cobros a liberar: <strong className="font-mono">{estado.aLiberar}</strong></span>
            {estado.enCurso && <Badge tone="amber">actualizando…</Badge>}
            {estado.ok === false && <Badge tone="red">con error</Badge>}
          </div>
          {r && (
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-500">
              <span>Órdenes nuevas: <span className="font-mono">{r.ordenesNuevas}</span></span>
              <span>Reconsultas: <span className="font-mono">{r.reconsultas}</span></span>
              <span>Consultas: <span className="font-mono">{r.consultas}</span></span>
              <span>Guardados: <span className="font-mono">{r.guardados}</span></span>
              <span>Errores: <span className="font-mono">{r.errores}</span></span>
              <span>Pendientes: <span className="font-mono">{r.pendientes}</span></span>
              {r.cortadoPorLimite && <span className="text-amber-700">MercadoPago pidió esperar (429): sigue en la próxima corrida</span>}
            </div>
          )}
          {r?.error && <p className="text-xs text-red-600">{r.error}</p>}
          {esAdmin && (
            <div className="flex justify-end">
              <Button onClick={actualizar} loading={iniciando} disabled={estado.enCurso}>Actualizar cobros de MercadoPago</Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
